import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, Link } from 'react-router-dom';
import { FiBox, FiCheckCircle, FiEdit2, FiHeart, FiMessageCircle, FiX, FiXCircle } from 'react-icons/fi';
import { UserContext } from '../../context/UserContext';
import { useNotifications } from '../../components/Notifications/NotificationsContext';
import { apiFetch, apiJson } from '../../utils/apiClient';
import FooterSimple from '../../components/Footer/FooterSimple';
import DeleteAccountPopup from '../../components/DeleteAccountPopup/DeleteAccountPopup';
import PageLoader from '../../components/PageLoader/PageLoader';
import './ProfilePage.css';

const formatStars = (rating) => {
    const rounded = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    return `${'★'.repeat(rounded)}${'☆'.repeat(5 - rounded)}`;
};

const getNotificationMessage = (notification) => {
    const actor = notification.actorUsername ?? notification.ActorUsername ?? 'User';
    const title = notification.itemTitle ?? notification.ItemTitle ?? 'your item';
    const type = (notification.type ?? notification.Type ?? '').toLowerCase();
    if (type === 'comment') {
        return `${actor} commented on "${title}"`;
    }
    if (type === 'listing_approved') {
        return `Your listing "${title}" was accepted`;
    }
    if (type === 'listing_rejected') {
        return `Your listing "${title}" was rejected`;
    }
    if (type === 'item_sold_to_you') {
        return `${actor} sold you "${title}" — it's in your collection`;
    }
    return `${actor} liked "${title}"`;
};

const getNotificationIcon = (type) => {
    if (type === 'comment') return <FiMessageCircle />;
    if (type === 'listing_approved') return <FiCheckCircle />;
    if (type === 'listing_rejected') return <FiXCircle />;
    if (type === 'item_sold_to_you') return <FiBox />;
    return <FiHeart />;
};

const getNotificationNotePreview = (notification) => {
    const raw = (notification.message ?? notification.Message ?? '').trim();
    if (!raw) return '';
    const firstLine = raw.split(/\r?\n/)[0].trim();
    if (!firstLine) return '';
    const hasMore = raw.includes('\n') && raw.trim() !== firstLine;
    return hasMore ? `${firstLine}...` : firstLine;
};

const ProfilePage = () => {

    const navigate = useNavigate();
    const { user, setUser } = useContext(UserContext);
    const [userChecked, setUserChecked] = useState(false);
    const hasFetchedUserProfileData = useRef(false);
    const [loading, setLoading] = useState(false);
    const [userProfileData, setUserProfileData] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [reviewsLoading, setReviewsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [lastNameDraft, setLastNameDraft] = useState('');
    const [lastNameSaving, setLastNameSaving] = useState(false);
    const [lastNameError, setLastNameError] = useState('');
    const [editingLastName, setEditingLastName] = useState(false);
    const {
        notifications,
        loading: notificationsLoading,
        clearingAll: clearingAllNotifications,
        markAllRead,
        clearNotification,
        clearAllNotifications,
    } = useNotifications();

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (!user && storedUser) {
            setUser(JSON.parse(storedUser));
        } else if (!user) {
            navigate('/login', {
                replace: true,
                state: { redirectTo: `/profilepage` }
            });
        }
        setUserChecked(true);
    }, [user, setUser, navigate]);

    useEffect(() => {
        if (user && !hasFetchedUserProfileData.current) {
            hasFetchedUserProfileData.current = true;

            const fetchSessionData = async () => {
                setLoading(true);
                try {
                    const requestBody = { userId: user.userId };
                    const response = await apiFetch('/Users/GetUserProfileData', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(requestBody),
                    });

                    if (!response.ok) throw new Error('Failed to fetch user profile data');

                    const data = await response.json();
                    if (data) {
                        setUserProfileData(data);
                        const existingLastName = (data.lastName || data.LastName || '').trim();
                        setLastNameDraft(existingLastName);
                        setEditingLastName(!existingLastName);
                    } else setError('No user profile data found or access denied.');
                } catch (err) {
                    setError(err.message);
                } finally {
                    setLoading(false);
                }
            };

            fetchSessionData();
        }
    }, [user]);

    useEffect(() => {
        if (!user?.userId) return undefined;

        let cancelled = false;
        const fetchReviews = async () => {
            setReviewsLoading(true);
            try {
                const data = await apiJson('/MarketplaceReviews/GetUserReviews', {
                    body: {
                        userId: Number(user.userId),
                        pageSize: 20,
                    },
                });
                if (!cancelled) {
                    setReviews(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                console.error(err);
                if (!cancelled) setReviews([]);
            } finally {
                if (!cancelled) setReviewsLoading(false);
            }
        };

        fetchReviews();
        return () => {
            cancelled = true;
        };
    }, [user?.userId]);

    useEffect(() => {
        if (!user?.userId) return undefined;
        markAllRead();
        return undefined;
    }, [user?.userId, notifications, markAllRead]);

    const handleClearNotification = async (event, notificationId) => {
        event.preventDefault();
        event.stopPropagation();
        await clearNotification(notificationId);
    };

    const handleClearAllNotifications = async () => {
        await clearAllNotifications();
    };

    const handleNotificationClick = (notification) => {
        const type = (notification?.type ?? notification?.Type ?? '').toLowerCase();
        const collectionItemId = Number(
            notification?.collectionItemId ?? notification?.CollectionItemId ?? 0
        );
        const marketplaceItemId = Number(
            notification?.marketplaceItemId ?? notification?.MarketplaceItemId ?? 0
        );

        if (type === 'listing_approved' || type === 'listing_rejected') {
            // Listing review notifications store the marketplace item id for deep links.
            const listingId = marketplaceItemId > 0 ? marketplaceItemId : collectionItemId;
            if (listingId > 0) {
                navigate(`/marketplaceItem/${listingId}`);
                return;
            }
            navigate('/mymarketplacelistings');
            return;
        }

        if (!collectionItemId) return;
        navigate(`/collectionItem/${collectionItemId}`);
    };

    const handleDeleteAccountClick = () => {
        setShowDeletePopup(true);
    };

    const handleCancelDelete = () => {
        setShowDeletePopup(false);
    };

    const handleSaveLastName = async () => {
        const trimmed = lastNameDraft.trim();
        if (!trimmed) {
            setLastNameError('Last name is required');
            return;
        }

        setLastNameSaving(true);
        setLastNameError('');
        try {
            const response = await apiFetch('/Users/UpdateLastName', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ lastName: trimmed }),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok || result.success === false) {
                setLastNameError(result.errorMessage || result.ErrorMessage || 'Unable to save last name.');
                return;
            }
            const saved = result.lastName || result.LastName || trimmed;
            setLastNameDraft(saved);
            setUserProfileData((prev) => (prev ? { ...prev, lastName: saved } : prev));
            setEditingLastName(false);
        } catch (err) {
            console.error(err);
            setLastNameError('Unable to save last name. Please try again.');
        } finally {
            setLastNameSaving(false);
        }
    };

    const startEditingLastName = () => {
        setLastNameDraft((userProfileData?.lastName || userProfileData?.LastName || lastNameDraft || '').trim());
        setLastNameError('');
        setEditingLastName(true);
    };

    const cancelEditingLastName = () => {
        setLastNameDraft((userProfileData?.lastName || userProfileData?.LastName || '').trim());
        setLastNameError('');
        setEditingLastName(false);
    };

    const handleConfirmDelete = async () => {
        try {
            setLoading(true);

            const requestBody = {
                email: userProfileData?.email
            };

            const response = await apiFetch('/Users/DeleteAccount', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            if (response.ok) {
                localStorage.removeItem('user');
                setUser(null);
                navigate('/', { replace: true });
            } else {
                let errorMessage = 'Delete request was unsuccessful. Please try again.';

                try {
                    const errorText = await response.text();
                    if (errorText) {
                        errorMessage = errorText;
                    }
                } catch (textError) {
                    console.log('Could not read error response text:', textError);
                }

                alert(errorMessage);
            }
        } catch (error) {
            console.error('Error deleting account:', error);
            alert('Delete request was unsuccessful. Please try again.');
        } finally {
            setLoading(false);
            setShowDeletePopup(false);
        }
    };

    if (!userChecked || loading || (!userProfileData && !error)) {
        return <PageLoader />;
    }

    if (error && !userProfileData) {
        return (
            <div className="profile-page-shell">
                <div className="profile-page-inner">
                    <section className="profile-panel">
                        <h2 className="profile-section-title">Profile unavailable</h2>
                        <p className="profile-load-error">{error}</p>
                        <p className="profile-load-error-hint">
                            Make sure the API is running, then refresh this page.
                        </p>
                        <button
                            type="button"
                            className="profile-last-name-save"
                            onClick={() => window.location.reload()}
                        >
                            Refresh
                        </button>
                    </section>
                </div>
                <FooterSimple />
            </div>
        );
    }

    const avgRating = Number(userProfileData?.marketplaceAverageRating ?? 0);
    const reviewCount = userProfileData?.marketplaceReviewCount ?? 0;
    const itemsSold = userProfileData?.marketplaceItemsSoldCount ?? 0;

    return (
        <div className="profile-page-shell">
            <div className="profile-page-inner">
                <section className="profile-hero">
                    <p className="profile-hero-eyebrow">Driver Profile</p>
                    <h1 className="profile-hero-name">{userProfileData?.username || 'N/A'}</h1>
                    <div className="profile-hero-trust" aria-label="Marketplace trust summary">
                        <span className="profile-hero-trust-item">
                            <span className="profile-hero-trust-stars" aria-hidden="true">{formatStars(avgRating)}</span>
                            <span className="profile-hero-trust-value">{avgRating.toFixed(1)}</span>
                            <span className="profile-hero-trust-label">Avg rating</span>
                        </span>
                        <span className="profile-hero-trust-sep" aria-hidden="true" />
                        <span className="profile-hero-trust-item">
                            <span className="profile-hero-trust-value">{reviewCount}</span>
                            <span className="profile-hero-trust-label">Reviews</span>
                        </span>
                        <span className="profile-hero-trust-sep" aria-hidden="true" />
                        <span className="profile-hero-trust-item">
                            <span className="profile-hero-trust-value">{itemsSold}</span>
                            <span className="profile-hero-trust-label">Sold</span>
                        </span>
                    </div>
                </section>

                <div className="profile-outer-grid">
                    <div className="profile-main-column">
                <div className="profile-layout-grid">
                    <section className="profile-panel profile-panel--account">
                        <h2 className="profile-section-title">Account</h2>
                        <dl className="profile-account-list">
                            <div className="profile-account-row">
                                <dt>Username</dt>
                                <dd>{userProfileData?.username || 'N/A'}</dd>
                            </div>
                            <div className="profile-account-row">
                                <dt>First Name</dt>
                                <dd>{userProfileData?.firstName || 'N/A'}</dd>
                            </div>
                            <div className={`profile-account-row${editingLastName ? ' profile-account-row--last-name' : ''}`}>
                                <dt>Last Name</dt>
                                <dd>
                                    {editingLastName ? (
                                        <>
                                            <div className="profile-last-name-edit">
                                                <input
                                                    type="text"
                                                    name="lastName"
                                                    value={lastNameDraft}
                                                    onChange={(e) => {
                                                        setLastNameDraft(e.target.value);
                                                        if (lastNameError) setLastNameError('');
                                                    }}
                                                    placeholder="Add your last name"
                                                    autoComplete="family-name"
                                                    className={lastNameError ? 'error' : ''}
                                                    disabled={lastNameSaving}
                                                />
                                                <button
                                                    type="button"
                                                    className="profile-last-name-save"
                                                    onClick={handleSaveLastName}
                                                    disabled={lastNameSaving}
                                                >
                                                    {lastNameSaving ? 'Saving…' : 'Save'}
                                                </button>
                                                {(userProfileData?.lastName || userProfileData?.LastName) ? (
                                                    <button
                                                        type="button"
                                                        className="profile-last-name-cancel"
                                                        onClick={cancelEditingLastName}
                                                        disabled={lastNameSaving}
                                                    >
                                                        Cancel
                                                    </button>
                                                ) : null}
                                            </div>
                                            {lastNameError ? (
                                                <p className="profile-last-name-feedback is-error">{lastNameError}</p>
                                            ) : null}
                                        </>
                                    ) : (
                                        <div className="profile-last-name-display">
                                            <span>{userProfileData?.lastName || userProfileData?.LastName || 'N/A'}</span>
                                            <button
                                                type="button"
                                                className="profile-field-edit-btn"
                                                onClick={startEditingLastName}
                                                aria-label="Edit last name"
                                                title="Edit last name"
                                            >
                                                <FiEdit2 aria-hidden />
                                            </button>
                                        </div>
                                    )}
                                </dd>
                            </div>
                            <div className="profile-account-row">
                                <dt>Email</dt>
                                <dd>{userProfileData?.email || 'N/A'}</dd>
                            </div>
                            <div className="profile-account-row profile-account-row--coins">
                                <dt>Collector Coins</dt>
                                <dd>
                                    <Link to="/auctions" className="profile-collector-coins-link">
                                        <span className="profile-collector-coins-value">
                                            {userProfileData?.collectorCoinBalance ?? 0}
                                        </span>
                                        <span className="profile-collector-coins-meta">
                                            {userProfileData?.collectorCoinMonthlyScore ?? 0} earned this month
                                        </span>
                                    </Link>
                                </dd>
                            </div>
                        </dl>
                    </section>

                    <section className="profile-panel profile-panel--value">
                        <h2 className="profile-section-title">Collection Value</h2>
                        <div className="profile-value-metrics">
                            <div className="profile-value-metric">
                                <div className="profile-value-metric-amount">
                                    $ {Number(userProfileData?.totalCostOfCollection ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                <div className="profile-value-metric-label">Collection Cost</div>
                            </div>
                            <div className="profile-value-metric">
                                <div className="profile-value-metric-amount">
                                    $ {Number(userProfileData?.totalValueOfCollection ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                <div className="profile-value-metric-label">Collection Value</div>
                            </div>
                        </div>
                    </section>
                </div>

                <section className="profile-panel">
                    <h2 className="profile-section-title">Collection Stats</h2>
                    <div className="collection-stats">
                        <div className="collection-stats-grid">
                            <Link to="/myitems?type=collectibles&page=1" className="collection-stat-link">
                                <div className="collection-stat-box">
                                    <div className="collection-stat-value">{userProfileData?.numberOfCollectibles ?? 0}</div>
                                    <div className="collection-stat-label">Collectibles</div>
                                </div>
                            </Link>
                            <Link to="/myitems?type=collectibles&hasLikes=1&page=1" className="collection-stat-link">
                                <div className="collection-stat-box">
                                    <div className="collection-stat-value">{userProfileData?.numberOfLikes ?? 0}</div>
                                    <div className="collection-stat-label">Total Likes</div>
                                </div>
                            </Link>
                            <Link to="/myitems?type=collectibles&driver=&series=&mainCategory=Race+Used+%2F+Team+Issued&subCategory=&subSubCategory=&subSubSubCategory=&page=1" className="collection-stat-link">
                                <div className="collection-stat-box">
                                    <div className="collection-stat-value">{userProfileData?.numberOfRaceUsedCollectibles ?? 0}</div>
                                    <div className="collection-stat-label">Race Used / Team Issued</div>
                                </div>
                            </Link>
                            <Link to="/myitems?type=diecasts&page=1" className="collection-stat-link">
                                <div className="collection-stat-box">
                                    <div className="collection-stat-value">{userProfileData?.numberOfDiecastCollectibles ?? 0}</div>
                                    <div className="collection-stat-label">Diecasts</div>
                                </div>
                            </Link>
                            <Link to="/myitems?type=collectibles&driver=&series=&mainCategory=Trading+Cards&subCategory=&subSubCategory=&subSubSubCategory=&page=1" className="collection-stat-link">
                                <div className="collection-stat-box">
                                    <div className="collection-stat-value">{userProfileData?.numberOfTradingCardCollectibles ?? 0}</div>
                                    <div className="collection-stat-label">Trading Cards</div>
                                </div>
                            </Link>
                            <Link to="/myitems?type=collectibles&driver=&series=&mainCategory=Hero+Cards+%2F+Post+Cards&subCategory=&subSubCategory=&subSubSubCategory=&page=1" className="collection-stat-link">
                                <div className="collection-stat-box">
                                    <div className="collection-stat-value">{userProfileData?.numberOfHeroCardCollectibles ?? 0}</div>
                                    <div className="collection-stat-label">Hero Cards</div>
                                </div>
                            </Link>
                            <Link to="/myitems?type=collectibles&driver=&series=&mainCategory=Programs+%2F+Tickets&subCategory=&subSubCategory=&subSubSubCategory=&page=1" className="collection-stat-link">
                                <div className="collection-stat-box">
                                    <div className="collection-stat-value">{userProfileData?.numberOfProgramTicketCollectibles ?? 0}</div>
                                    <div className="collection-stat-label">Programs & Tickets</div>
                                </div>
                            </Link>
                            <Link to="/auctions" className="collection-stat-link">
                                <div className="collection-stat-box">
                                    <div className="collection-stat-value">{userProfileData?.collectorCoinBalance ?? 0}</div>
                                    <div className="collection-stat-label">Collector Coins</div>
                                    <div className="collection-stat-sublabel">
                                        {userProfileData?.collectorCoinMonthlyScore ?? 0} this month
                                    </div>
                                </div>
                            </Link>
                        </div>
                    </div>
                </section>
                    </div>

                    <aside className="profile-panel profile-notifications-panel" aria-label="Notifications">
                        <div className="profile-notifications-header">
                            <h2 className="profile-section-title">Notifications</h2>
                            {notifications.length > 0 ? (
                                <button
                                    type="button"
                                    className="profile-notifications-clear-all"
                                    onClick={handleClearAllNotifications}
                                    disabled={clearingAllNotifications}
                                >
                                    {clearingAllNotifications ? 'Clearing…' : 'Clear all'}
                                </button>
                            ) : null}
                        </div>
                        <div className="profile-notifications-list">
                            {notificationsLoading ? (
                                <p className="profile-notifications-empty">Loading notifications…</p>
                            ) : notifications.length === 0 ? (
                                <p className="profile-notifications-empty">No notifications yet.</p>
                            ) : (
                                notifications.map((notification) => {
                                    const notificationId = notification.notificationId ?? notification.NotificationId;
                                    const type = (notification.type ?? notification.Type ?? '').toLowerCase();
                                    const dateLabel = notification.dateFormatted ?? notification.DateFormatted ?? '';
                                    const notePreview = getNotificationNotePreview(notification);
                                    return (
                                        <button
                                            key={notificationId}
                                            type="button"
                                            className="profile-notif-row"
                                            onClick={() => handleNotificationClick(notification)}
                                        >
                                            <span className="profile-notif-icon" aria-hidden="true">
                                                {getNotificationIcon(type)}
                                            </span>
                                            <span className="profile-notif-body">
                                                <span className="profile-notif-message">
                                                    {getNotificationMessage(notification)}
                                                </span>
                                                {notePreview ? (
                                                    <span className="profile-notif-reason">
                                                        {notePreview}
                                                    </span>
                                                ) : null}
                                                {dateLabel ? (
                                                    <time className="profile-notif-date">{dateLabel}</time>
                                                ) : null}
                                            </span>
                                            <span
                                                role="button"
                                                tabIndex={0}
                                                className="profile-notif-clear"
                                                aria-label="Clear notification"
                                                onClick={(event) => handleClearNotification(event, notificationId)}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault();
                                                        handleClearNotification(event, notificationId);
                                                    }
                                                }}
                                            >
                                                <FiX aria-hidden />
                                            </span>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </aside>
                </div>

                <section className="profile-panel profile-panel--reviews">
                    <h2 className="profile-section-title">Marketplace Reviews</h2>
                    <div className="profile-reviews-list">
                        {reviewsLoading ? (
                            <p className="profile-reviews-empty">Loading reviews…</p>
                        ) : reviews.length === 0 ? (
                            <p className="profile-reviews-empty">No reviews yet.</p>
                        ) : (
                            reviews.map((review) => {
                                const rating = Number(review.rating ?? review.Rating ?? 0);
                                const username = review.reviewerUsername ?? review.ReviewerUsername ?? 'User';
                                const dateLabel = review.datePostedFormatted ?? review.DatePostedFormatted ?? '';
                                const text = review.reviewText ?? review.ReviewText ?? '';
                                const id = review.reviewId ?? review.ReviewId;
                                return (
                                    <article key={id} className="profile-review-row">
                                        <div className="profile-review-meta">
                                            <div className="profile-review-author-line">
                                                <span className="profile-review-author">{username}</span>
                                                <span className="profile-review-stars" aria-label={`${rating} stars`}>
                                                    {formatStars(rating)}
                                                </span>
                                            </div>
                                            {dateLabel ? (
                                                <time className="profile-review-date">{dateLabel}</time>
                                            ) : null}
                                        </div>
                                        {text ? (
                                            <p className="profile-review-text">{text}</p>
                                        ) : null}
                                    </article>
                                );
                            })
                        )}
                    </div>
                </section>

                <p
                    className="delete-account-link"
                    onClick={handleDeleteAccountClick}
                >
                    Delete My Account
                </p>
            </div>
            <FooterSimple />
            <DeleteAccountPopup
                show={showDeletePopup}
                onCancel={handleCancelDelete}
                onDelete={handleConfirmDelete}
            />
        </div>
    );
};

export default ProfilePage;
